import React, { useState, useContext, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { SessionContextStore } from '../../../context/SessionContext'
import { ChevronUpIcon } from './assets'
import './styles.scss'
import { getEstimatedMilesOptions } from '../../../services/utils'
import { SessionService } from '../../../services/SessionService'

export const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = event => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('touchstart', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, handler])
}

export const useDrawer = (startOpen = false) => {
  const [open, setOpen] = useState(startOpen)

  const Drawer = useCallback(
    ({ children, height }) => (
      <div className="drawer-container">
        <div className={`drawer ${open ? 'open' : ''}`} style={{ height }}>
          {children}
        </div>
      </div>
    ),
    [open, setOpen]
  )

  return { open, setOpen, Drawer }
}

export const Collapse = props => {
  const [open, setOpen] = useState(false)
  return (
    <div
      className="collapse"
      style={{
        height: open ? props.open : props.close,
      }}
    >
      <div
        className="flex align-center"
        style={{ fontSize: '12px', width: '100%', height: '1.5rem', marginBottom: '.5rem' }}
        onClick={() => !props.disabled && setOpen(!open)}
      >
        <div style={{ flexGrow: 1, ...(props.color && { color: props.color }) }}>{props.title}</div>
        {!props.disabled && (
          <div style={{ marginLeft: 'auto', position: 'relative', width: '1rem' }}>
            <ChevronUpIcon
              color={props.color}
              style={{
                position: 'absolute',
                margin: 'auto',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                rotate: open ? '0deg' : '180deg',
                transition: '.3s ease-in-out',
              }}
            />
          </div>
        )}
      </div>

      <div>{props.children}</div>
    </div>
  )
}

export const MilesRangeSlider = ({ vehicle, affiliateCode }) => {
  const { dispatch } = useContext(SessionContextStore)
  const { estimated_mileage_value, make, model, year } = vehicle
  const isAdmin = SessionService.isAdmin()
  const estimatedMileageOption = getEstimatedMilesOptions(affiliateCode, isAdmin).estimatedMileageOption
  const min = 0,
    max = estimatedMileageOption.length - 1,
    step = 1
  const [value, setValue] = useState(estimated_mileage_value)
  const setStyle = () => ({
    backgroundSize: `${((estimatedMileageOption.findIndex(val => val === value) - min) * 100) / (max - min)}% 100%`,
  })
  const handleChange = e => {
    const newValue = estimatedMileageOption[e.target.valueAsNumber]
    const updatedVehicle = { ...vehicle, estimated_mileage_value: newValue }
    setValue(newValue)
    dispatch({ type: 'UPDATE_VEHICLE', data: updatedVehicle })
  }
  return (
    <div
      className="flex flex-col justify-between"
      style={{
        height: '90px',
        borderBottom: '1px solid #e5dfd8',
        marginBottom: '.5rem',
      }}
    >
      <div className="flex justify-between" style={{ fontSize: '12px', fontWeight: '500' }}>
        <div className="truncate" style={{ marginBottom: '.5rem' }}>
          {make} | {model} | {year}
        </div>
        <output style={{ flexShrink: 0, marginLeft: '1rem' }}>{value} miles</output>
      </div>
      <div>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={estimatedMileageOption.findIndex(val => val === value)}
          onChange={handleChange}
          style={setStyle()}
        />
        <div className="flex justify-between" style={{ fontSize: '10px', marginBottom: '.5rem' }}>
          <div>{estimatedMileageOption[min]} miles / year</div>
          <div>{estimatedMileageOption[max]} miles / year</div>
        </div>
      </div>
    </div>
  )
}

export const Portal = ({ children }) => {
  const portal = document.getElementById('tooltips')
  const el = document.createElement('div')

  useEffect(() => {
    portal.appendChild(el)
    return () => portal.removeChild(el)
  }, [el, portal])

  return createPortal(children, el)
}

export const Tooltip = ({ text, children }) => {
  const ref = useRef()
  const [coords, setCoords] = useState({ left: 0, top: 0 })
  const [showTooltip, setShowTooltip] = useState(false)
  useOnClickOutside(ref, () => setShowTooltip(false))
  const mouseEnter = e => {
    const rect = e.target.getBoundingClientRect()
    setCoords({
      left: '20%',
      top: rect.top + rect.height + 5,
    })
  }

  return (
    <div
      ref={ref}
      onMouseEnter={mouseEnter}
      onClick={e => {
        mouseEnter(e)
        setShowTooltip(!showTooltip)
      }}
      style={{ display: 'flex', justifyContent: 'center' }}
    >
      {children}
      {showTooltip && (
        <Portal>
          <div style={{ left: coords.left, top: coords.top }} className="tooltip">
            {text}
          </div>
        </Portal>
      )}
    </div>
  )
}
